import React from "react";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons";
import tw, { TwStyle } from "twin.macro";
import styled from "styled-components";
import StatsIllustrationSrc from "images/stats-illustration.svg";
import { ReactComponent as SvgDotPattern } from "images/dot-pattern.svg";

const SectionHeading = tw.h2`text-4xl sm:text-5xl font-black tracking-wide text-center`;
const SubheadingBase = tw.h5`font-bold text-primary-500`;

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Statistic {
  key: string;
  value: string;
}

export interface TwoColStatsProps {
  subheading?: React.ReactNode;
  heading?: React.ReactNode;
  description?: React.ReactNode;
  primaryButtonText?: string;
  primaryButtonUrl?: string;
  imageSrc?: string;
  imageCss?: TwStyle | null;
  imageContainerCss?: TwStyle | null;
  imageDecoratorBlob?: boolean;
  imageDecoratorBlobCss?: TwStyle | null;
  imageInsideDiv?: boolean;
  statistics?: Statistic[];
  textOnLeft?: boolean;
}

// ─── Styled ──────────────────────────────────────────────────────────────────

const Container = tw.div`relative`;
const TwoColumn = tw.div`flex flex-col md:flex-row justify-between max-w-screen-xl mx-auto py-20 md:py-24`;
const Column = tw.div`w-full max-w-md mx-auto md:max-w-none md:mx-0`;
const ImageColumn = tw(Column)`md:w-5/12 flex-shrink-0 h-80 md:h-auto relative`;
const TextColumn = styled(Column)<{ textOnLeft?: boolean }>((props) => [
  tw`md:w-7/12 mt-16 md:mt-0`,
  props.textOnLeft
    ? tw`md:mr-12 lg:mr-16 md:order-first`
    : tw`md:ml-12 lg:ml-16 md:order-last`,
]);

const Image = styled.div<{ imageSrc: string }>((props) => [
  `background-image: url("${props.imageSrc}");`,
  tw`rounded bg-contain bg-no-repeat bg-center h-full`,
]);
const TextContent = tw.div`lg:py-8 text-center md:text-left`;

const Subheading = tw(SubheadingBase)`text-center md:text-left`;
const Heading = tw(
  SectionHeading,
)`mt-4 font-black text-left text-3xl sm:text-4xl lg:text-5xl text-center md:text-left leading-tight`;
const StatsDescription = tw.p`mt-4 text-center md:text-left text-sm md:text-base lg:text-lg font-medium leading-relaxed text-secondary-100`;

const Statistics = tw.div`grid grid-cols-2 gap-6 mt-8`;
const StatItem = styled.div`
  ${tw`text-left pl-4`}
  border-left: 3px solid #ff6c00;
`;
const Value = styled.div`
  font-size: 2rem;
  font-weight: 900;
  line-height: 1;
  color: #ff6c00;
  margin-bottom: 0.35rem;
`;
const Key = tw.div`text-sm font-semibold uppercase tracking-widest text-secondary-500`;

const PrimaryButton = tw(
  PrimaryButtonBase,
)`mt-8 md:mt-10 text-sm inline-block mx-auto md:mx-0`;

const DecoratorBlob = styled(SvgDotPattern)`
  ${tw`w-20 h-20 absolute right-0 bottom-0 transform translate-x-1/2 translate-y-1/2 fill-current text-primary-500 -z-10`}
`;

// ─── Default data ─────────────────────────────────────────────────────────────

const DEFAULT_STATISTICS: Statistic[] = [
  { key: "Clients", value: "2282+" },
  { key: "Projects", value: "3891+" },
  { key: "Awards", value: "1000+" },
];

// ─── Component ───────────────────────────────────────────────────────────────

const TwoColSingleFeatureWithStats2: React.FC<TwoColStatsProps> = ({
  subheading = "Our Track Record",
  heading = (
    <>
      We have been doing this <wbr /> since{" "}
      <span tw="text-primary-500">1999.</span>
    </>
  ),
  description = "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
  primaryButtonText = "Learn More",
  primaryButtonUrl = "/",
  imageSrc = StatsIllustrationSrc,
  imageCss = null,
  imageContainerCss = null,
  imageDecoratorBlob = false,
  imageDecoratorBlobCss = null,
  imageInsideDiv = true,
  statistics,
  textOnLeft = false,
}) => {
  const stats = statistics ?? DEFAULT_STATISTICS;

  return (
    <Container>
      <TwoColumn css={!imageInsideDiv && tw`md:items-center`}>
        <ImageColumn css={imageContainerCss}>
          {imageInsideDiv ? (
            <Image imageSrc={imageSrc!} css={imageCss as any} />
          ) : (
            <img src={imageSrc} css={imageCss as any} alt="" />
          )}
          {imageDecoratorBlob && (
            <DecoratorBlob css={imageDecoratorBlobCss as any} />
          )}
        </ImageColumn>

        <TextColumn textOnLeft={textOnLeft}>
          <TextContent>
            {subheading && <Subheading>{subheading}</Subheading>}
            <Heading>{heading}</Heading>
            <StatsDescription>{description}</StatsDescription>{" "}
            <Statistics>
              {stats.map((stat) => (
                <StatItem key={stat.key}>
                  <Value>{stat.value}</Value>
                  <Key>{stat.key}</Key>
                </StatItem>
              ))}
            </Statistics>
            <PrimaryButton as="a" href={primaryButtonUrl}>
              {primaryButtonText}
            </PrimaryButton>
          </TextContent>
        </TextColumn>
      </TwoColumn>
    </Container>
  );
};

export default TwoColSingleFeatureWithStats2;
